import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Users, UserPlus, Activity, UserX, Send, AlertTriangle, Megaphone } from "lucide-react";

const PERIODS = [
  { key: "hoje", label: "Hoje" },
  { key: "7dias", label: "7 dias" },
  { key: "30dias", label: "30 dias" },
  { key: "mes", label: "Este mês" },
  { key: "custom", label: "Personalizado" },
];

const CARDS = [
  { key: "clientes_cadastrados", label: "Clientes cadastrados", icon: UserPlus, color: "text-sky-600", bg: "bg-sky-50" },
  { key: "clientes_total", label: "Total de clientes", icon: Users, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "clientes_ativos", label: "Clientes ativos", icon: Activity, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "clientes_inativos", label: "Clientes inativos", icon: UserX, color: "text-slate-500", bg: "bg-slate-100" },
  { key: "mensagens_enviadas", label: "Mensagens enviadas", icon: Send, color: "text-indigo-600", bg: "bg-indigo-50" },
  { key: "mensagens_erro", label: "Mensagens com erro", icon: AlertTriangle, color: "text-red-500", bg: "bg-red-50" },
  { key: "campanhas", label: "Campanhas realizadas", icon: Megaphone, color: "text-amber-600", bg: "bg-amber-50" },
];

export default function Reports() {
  const [period, setPeriod] = useState("30dias");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const params = { period };
    if (period === "custom" && range.start && range.end) { params.start = range.start; params.end = range.end; }
    api.get("/reports", { params }).then((r) => setData(r.data));
  }, [period, range]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const p = new URLSearchParams({ period });
    if (period === "custom" && range.start && range.end) { p.set("start", range.start); p.set("end", range.end); }
    window.open(`${API}/reports/export?${p.toString()}`, "_blank");
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div><h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Relatórios</h1><p className="text-sm text-slate-500 mt-1">Indicadores da clínica por período.</p></div>
        <Button onClick={exportCsv} className="bg-emerald-500 hover:bg-emerald-600" data-testid="reports-export-button"><Download className="h-4 w-4 mr-2" /> Exportar CSV</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`report-period-${p.key}`}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border ${period === p.key ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"}`}>{p.label}</button>
        ))}
        {period === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-40" data-testid="report-start" />
            <span className="text-slate-400">até</span>
            <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-40" data-testid="report-end" />
          </div>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {CARDS.map((c) => (
            <div key={c.key} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid={`report-${c.key}`}>
              <div className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center mb-3`}><c.icon className={`h-5 w-5 ${c.color}`} /></div>
              <p className="text-2xl font-bold text-slate-900 font-display">{data[c.key]}</p>
              <p className="text-xs text-slate-500 mt-1">{c.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
